/**
 * ResultsMap Component
 *
 * Map view with listing markers.
 * Note: Google Maps integration requires API key configuration.
 * This is a placeholder implementation ready for map integration.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ListingResult } from '../hooks/useSearchResults';

interface ResultsMapProps {
  listings: ListingResult[];
  onSave?: (listingId: string) => void;
  savedIds?: string[];
}

export const ResultsMap: React.FC<ResultsMapProps> = ({
  listings,
  onSave: _onSave,
  savedIds: _savedIds = [],
}) => {
  const [selectedListing, setSelectedListing] = useState<ListingResult | null>(null);

  // Lebanon center coordinates
  const centerLat = 33.8547;
  const centerLng = 35.8623;

  return (
    <div className="relative w-full h-full min-h-[500px] bg-gray-100 rounded-xl overflow-hidden">
      {/* Map placeholder */}
      <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
        <div className="text-center">
          <MapIcon className="w-16 h-16 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">Map View</p>
          <p className="text-sm text-gray-400">
            Showing {listings.length} properties
          </p>
        </div>
      </div>

      {/* Price markers (positioned as visual representation) */}
      <div className="absolute inset-0 p-8">
        {listings.slice(0, 8).map((listing) => {
          // Generate pseudo-random positions based on coordinates
          const lat = listing.location.coordinates?.latitude || centerLat;
          const lng = listing.location.coordinates?.longitude || centerLng;
          const top = ((lat - 33.5) / 1.5) * 100;
          const left = ((lng - 35.4) / 1) * 100;

          return (
            <button
              key={listing.id}
              onClick={() => setSelectedListing(listing)}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 px-2 py-1 rounded-full text-xs font-semibold transition-all shadow-md ${
                selectedListing?.id === listing.id
                  ? 'bg-gray-900 text-white scale-110 z-10'
                  : 'bg-white text-gray-900 hover:scale-105'
              }`}
              style={{
                top: `${Math.max(10, Math.min(90, top))}%`,
                left: `${Math.max(10, Math.min(90, left))}%`,
              }}
            >
              ${listing.pricing.nightlyRate}
            </button>
          );
        })}
      </div>

      {/* Selected listing card */}
      {selectedListing && (
        <div className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white rounded-xl shadow-lg overflow-hidden z-20">
          <button
            onClick={() => setSelectedListing(null)}
            className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 z-10"
            aria-label="Close"
          >
            <CloseIcon className="w-4 h-4 text-gray-500" />
          </button>

          <Link to={`/listing/${selectedListing.id}`}>
            <img
              src={selectedListing.photos[0]?.url}
              alt={selectedListing.title}
              className="w-full h-32 object-cover"
            />
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-900 text-sm truncate">
                  {selectedListing.title}
                </h3>
                {selectedListing.rating && (
                  <div className="flex items-center gap-1 text-xs shrink-0">
                    <StarIcon className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    <span>{selectedListing.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedListing.location.city}, {selectedListing.location.area}
              </p>
              <p className="text-sm mt-2">
                <span className="font-semibold">${selectedListing.pricing.nightlyRate}</span>
                <span className="text-gray-500"> / night</span>
              </p>
            </div>
          </Link>
        </div>
      )}

      {/* Map controls placeholder */}
      <div className="absolute top-4 right-4 flex flex-col gap-2">
        <button className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50">
          <PlusIcon className="w-5 h-5 text-gray-700" />
        </button>
        <button className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50">
          <MinusIcon className="w-5 h-5 text-gray-700" />
        </button>
      </div>
    </div>
  );
};

// Icons
const MapIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MinusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

export default ResultsMap;
